import React, { useState, useEffect } from 'react';
import ReactCrop from 'react-image-crop';
import styled from 'styled-components';
import loadImage from 'blueimp-load-image/js';
import { FormattedMessage } from 'react-intl';

import { Images } from '../../assets/index';
import './styles.scss';

import 'react-image-crop/dist/ReactCrop.css';
import { CustomRodal } from '../custom-rodal';
import { StepperButtons } from '../stepper-buttons';

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;

  .buttons {
    margin-top: 20px;
    margin-bottom: 20px;
    display: flex;
    justify-content: flex-end;
    button {
      height: 35px;
      width: 125px;
      color: #29435a;
      font-size: 18px;
      border: transparent;
      background: white;
      display: flex;
      justify-content: center;
      align-items: center;
    }
    .btn-cancel {
      margin-left: 20px;
    }
    .btn-crop {
      border-radius: 20px;
      background-color: #f4487e;
      font-weight: bold;
      color: white;
    }
  }
`;

const CropImage = styled.div`
  width: 100%;
  overflow: hidden;
  margin: auto;
  display: flex;
  justify-content: center;
  .ReactCrop {
    width: auto;
    height: 100%;
    div {
      height: 100%;
      width: auto;
      img {
        object-fit: contain;
        max-height: 400px;
        min-width: 100px;
        width: unset;
        height: unset;
      }
    }
  }
`;

export const UploadImage = ({ label, field, form, userRole, ...props }) => {
  const [logoModal, setLogoModal] = useState(false);
  const [upImg, setUpImg] = useState('');
  const [base64Img, setBase64Img] = useState('');
  const [imgRef, setImgRef] = useState(null);
  const [crop, setCrop] = useState({
    aspect: 1, // Default aspect ratio
    unit: '%',
    width: 200
  });

  useEffect(() => {
    if (field?.value) {
      setUpImg(field.value);
    }
  }, [field]);

  useEffect(() => {
    if (imgRef) {
      makeClientCrop(crop);
    }
  }, [imgRef]);

  const onLoad = (img) => {
    setImgRef(img);
  };

  const onSelectFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setCrop({
        ...crop,
        unit: '%',
        width: 200,
        height: 'unset'
      });

      loadImage(
        e.target.files[0],
        (img) => {
          const base64data = img.toDataURL('image/jpeg');
          setUpImg(base64data);
          setLogoModal(true);
        },
        { orientation: true, canvas: true }
      );
    }
  };

  const makeClientCrop = async (crop) => {
    if (imgRef && crop.width && crop.height) {
      await createCropPreview(imgRef, crop, 'newFile.jpeg');
    }
  };

  const createCropPreview = async (image, crop, fileName) => {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    // Set canvas dimensions to match the scaled crop area
    const scaledWidth = Math.ceil(crop.width * scaleX);
    const scaledHeight = Math.ceil(crop.height * scaleY);

    // Optionally resize the canvas dimensions to reduce size
    const maxDimension = 500; // Set a max dimension (e.g., 500px)
    const resizeRatio = Math.min(1, maxDimension / Math.max(scaledWidth, scaledHeight));
    canvas.width = Math.ceil(scaledWidth * resizeRatio);
    canvas.height = Math.ceil(scaledHeight * resizeRatio);

    const ctx = canvas.getContext('2d');

    // Draw the cropped area of the image onto the canvas
    ctx.drawImage(
      image,
      Math.ceil(crop.x * scaleX), // The x coordinate where to start clipping
      Math.ceil(crop.y * scaleY), // The y coordinate where to start clipping
      scaledWidth, // The width of the clipped image
      scaledHeight, // The height of the clipped image
      0, // The x coordinate where to place the image on the canvas
      0, // The y coordinate where to place the image on the canvas
      canvas.width, // Resized width on the canvas
      canvas.height // Resized height on the canvas
    );

    // Return the cropped image as a blob or base64
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Canvas is empty'));
            return;
          }

          // Generate a blob URL for preview
          blob.name = fileName;

          // Update state with the preview and base64 encoded image
          getBase64(blob).then((base64) => {
            setBase64Img(base64);
            resolve(base64); // Resolve with the base64-encoded image
          });
        },
        'image/jpeg',
        0.8 // Reduce quality to 80% to save space
      );
    });
  };

  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleFileChange = (e) => {
    onSelectFile(e);
  };

  const resetImage = (event) => {
    event.preventDefault();
    form.setFieldValue(field.name, '');
  };

  const imagePreview =
    field?.value || 'https://d1uj83pvrphe42.cloudfront.net/default/default_profile_pic.png';

  return (
    <div className="logo-upload-container">
      {/* Input Field Label */}
      <div className="input-field-label">{label}</div>

      {/* Logo Upload Circle */}
      <label htmlFor={`logo-upload-${field.name}`} className="upload-label">
        <div
          className="upload-circle"
          style={{
            background: form?.values?.[field.name] ? `url(${imagePreview})` : 'transparent'
          }}
        >
          {!form?.values?.[field.name] && <span className="plus-icon">+</span>}
          {form?.values?.[field.name] && (
            <img
              src={Images.binSvg}
              onClick={resetImage}
              alt={'delete'}
              className="delete-icon"
            ></img>
          )}
        </div>
      </label>

      {/* File Input */}
      <input
        id={`logo-upload-${field.name}`}
        type="file"
        accept="image/*"
        className="file-input"
        onChange={handleFileChange}
        hidden
      />

      {/* Modal for Image Cropping */}
      <CustomRodal
        visible={logoModal}
        onClose={() => setLogoModal(false)}
        style={{
          borderRadius: '10px',
          maxHeight: '570px',
          height: 'fit-content',
          width: '40%',
          zIndex: 1000000
        }}
        header={<FormattedMessage id="crop.header" />}
      >
        <ModalBody>
          <CropImage>
            <ReactCrop
              src={upImg}
              onImageLoaded={onLoad}
              crop={crop}
              onChange={(newCrop) => setCrop(newCrop)}
              onComplete={makeClientCrop}
            />
          </CropImage>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              width: 'min-content',
              margin: 'auto',
              marginTop: 24,
              marginBottom: 12
            }}
          >
            <StepperButtons
              secondaryProps={{
                onClick: () => setLogoModal(false),
                text: <FormattedMessage id="button.cancel" />,
                style: { width: 128 }
              }}
              wrapperStyle={{ justifyContent: 'end' }}
            />
            <StepperButtons
              type="button"
              primaryProps={{
                onClick: () => {
                  form.setFieldValue(field.name, base64Img);
                  setLogoModal(false);
                },
                text: <FormattedMessage id="button.crop" />,
                style: { width: 128 }
              }}
              wrapperStyle={{ justifyContent: 'start' }}
            />
          </div>
        </ModalBody>
      </CustomRodal>
    </div>
  );
};
